import { useEffect, type ReactElement, type ReactNode } from 'react';
import { HeadContent, Scripts, createRootRoute, useRouterState } from '@tanstack/react-router';
import { Analytics } from '~/components/analytics';
import { CookieBanner } from '~/components/layout/CookieBanner';
import { siteJsonLdScripts } from '~/lib/seo';
import globalCss from '~/styles/global.css?inline';

const NSAVE_CONSOLE_MARK = [
  '',
  '',
  '                       $$$$',
  '                  ╱ $$$$$$$$$$',
  '                 / $$$$____ $$$$',
  '                / $$$$     \\ $$$$',
  '               | $$$$       | $$$$',
  '               | $$$$       | $$$$',
  '               | $$$$       | $$$$',
  '               | $$$$       | $$$$',
  '               | $$$$       | $$$$',
  '               |____/       |____/',
  '              ',
  '                      nsave',
  '',
  'Join the eng team --> https://www.nsave.com/careers',
  '',
  '',
  '              tell us you saw this!',
].join('\n');
let hasShownConsoleMark = false;
const PAYLOAD_RTL_LANGUAGES = new Set(['ar', 'fa', 'he']);

export const Route = createRootRoute({
  head: ({ matches }) => {
    const isPayloadRoute = matches.some((match) => {
      const routeId = String(match.routeId);
      return routeId === '/_payload' || routeId.startsWith('/_payload/');
    });

    return {
      styles: isPayloadRoute ? [] : [{ children: globalCss }],
      links: [
        {
          rel: 'icon',
          href: '/favicon.ico',
        },
        ...(isPayloadRoute
          ? [
              {
                rel: 'preconnect' as const,
                href: 'https://fonts.googleapis.com',
              },
              {
                rel: 'preconnect' as const,
                href: 'https://fonts.gstatic.com',
                crossOrigin: 'anonymous' as const,
              },
              {
                rel: 'stylesheet' as const,
                href: 'https://fonts.googleapis.com/css2?family=Inter:wght@100..900&family=Roboto+Mono:wght@100..700&display=swap',
              },
            ]
          : [
              {
                rel: 'preload' as const,
                href: '/fonts/SuisseIntl-Book-latin-v1.woff2',
                as: 'font' as const,
                type: 'font/woff2',
                crossOrigin: 'anonymous' as const,
              },
              {
                rel: 'preload' as const,
                href: '/fonts/SuisseIntl-SemiBold-latin-v1.woff2',
                as: 'font' as const,
                type: 'font/woff2',
                crossOrigin: 'anonymous' as const,
              },
            ]),
      ],
      meta: [
        { charSet: 'utf-8' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1' },
        { title: "nsave — The world's bank" },
        {
          name: 'description',
          content:
            'Global USD, EUR & GBP accounts, cards, transfers and more — built for people in high-inflation economies.',
        },
      ],
      scripts: isPayloadRoute ? [] : siteJsonLdScripts(),
    };
  },
  shellComponent: WebsiteHtml,
});

/**
 * Lightweight equivalent of Payload's `withPayloadRoot` integration. Keeping
 * this wrapper local prevents `@payloadcms/ui` (DnD, Sonner, and admin CSS) from
 * entering the shared marketing bundle while preserving the admin document shell.
 */
function WebsiteHtml({ children }: { children: ReactNode }): ReactElement {
  const isAdminRoute: boolean = useRouterState({
    select: ({ location }) =>
      location.pathname === '/admin' || location.pathname.startsWith('/admin/'),
  });

  return isAdminRoute ? (
    <PayloadAdminHtml>{children}</PayloadAdminHtml>
  ) : (
    <MarketingHtml>{children}</MarketingHtml>
  );
}

function PayloadAdminHtml({ children }: { children: ReactNode }): ReactElement {
  const htmlProps = useRouterState({
    select: ({ matches }) => {
      for (const match of matches) {
        const data = match.loaderData as
          | { languageCode?: string; theme?: string }
          | null
          | undefined;
        if (data?.theme && data.languageCode) {
          return {
            'data-theme': data.theme,
            dir: PAYLOAD_RTL_LANGUAGES.has(data.languageCode) ? ('rtl' as const) : ('ltr' as const),
            lang: data.languageCode,
          };
        }
      }
      return { dir: 'ltr' as const };
    },
  });

  return (
    <html {...htmlProps} suppressHydrationWarning>
      <head>
        <style>{'@layer payload-default, payload;'}</style>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function MarketingHtml({ children }: { children: ReactNode }): ReactElement {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
        <ConsoleBrand />
        <Analytics />
        <CookieBanner />
      </body>
    </html>
  );
}

function ConsoleBrand() {
  useEffect(() => {
    if (hasShownConsoleMark) return;
    hasShownConsoleMark = true;
    // eslint-disable-next-line no-console -- Intentional nsave brand mark for browser DevTools.
    console.info(
      `%c${NSAVE_CONSOLE_MARK}`,
      'font-family: monospace; font-size: 13px; font-weight: 700; line-height: 1.05;',
    );
  }, []);

  return null;
}
